import { splitCamelCase } from "./formatting";

export interface DisplayedAttribute {
  /**
   * Distinct per row, built from the key path. Two adapters can write the same
   * field name, so a label may repeat where a React key may not.
   */
  id: string;
  label: string;
  value: string;
}

/** Spaces for underscores, since an adapter slug is snake_case. */
const toLabel = (segment: string) =>
  splitCamelCase(segment.replace(/_/g, " ")).trim();

/**
 * Flattens `coreBankingAttributes` into one displayable row per value.
 *
 * A core banking adapter decides the shape of what it writes there. Most values
 * are scalars, but an online banking registration nests one object per adapter —
 * `{ onlineBanking: { <adapter slug>: { username, … } } }` — and a nested value
 * stringifies to `[object Object]` unless it is descended into.
 *
 * The label names every segment of the path. Two adapters writing the same field
 * would otherwise produce identical labels against different values.
 *
 * Arrays are values rather than branches: they hold parallel scalars, like one
 * share id per account opened, so they render as one comma-separated row.
 */
const flattenCoreBankingAttributes = (
  attributes: unknown,
  path: string[] = []
): DisplayedAttribute[] => {
  // Only a plain object has entries worth walking. A primitive, an array, or null
  // arriving here means the caller passed something unexpected; there is nothing
  // to enumerate, so it yields no rows rather than throwing.
  if (
    !attributes ||
    typeof attributes !== "object" ||
    Array.isArray(attributes)
  ) {
    return [];
  }

  return Object.entries(attributes).flatMap(([key, value]) => {
    const keyPath = [...path, key];

    if (value && typeof value === "object" && !Array.isArray(value)) {
      return flattenCoreBankingAttributes(value, keyPath);
    }

    // `toString` covers an array too, joining on "," — which is what these rows
    // have always shown, so the separator is left alone.
    const display = value?.toString() ?? "";

    return [
      {
        id: keyPath.join("."),
        label: keyPath.map(toLabel).join(" "),
        value: display || "-",
      },
    ];
  });
};

export default flattenCoreBankingAttributes;
